import { DateTime } from 'luxon';
import { useMemo } from 'react';

import type { DateTimeRange } from '#components/shared/datePeriod.ts';
import type { Driver } from '#domain/Driver.ts';
import type { Tour } from '#domain/Tour.ts';
import type { Vehicle } from '#domain/Vehicle.ts';
import { filterStopInSearch } from '#utils/searchUtils.ts';

const containsSearchTerm = <T extends Tour>({
    tour,
    vehicles,
    drivers,
    search,
}: {
    tour: T;
    vehicles: Vehicle[];
    drivers?: Driver[];
    search: string;
}) => {
    const text = search.trim().toLowerCase();
    return (
        vehicles
            ?.find(vehicle => vehicle?.id === tour?.truck.id)
            ?.plate?.toLowerCase()
            .includes(text) ||
        drivers
            ?.find(driver => driver?.id === tour?.driverId)
            ?.name?.toLowerCase()
            .includes(text) ||
        tour.id?.toLowerCase().includes(text) ||
        tour.truck.id?.toLowerCase().includes(text) ||
        tour.searchName?.toLowerCase().includes(text) ||
        filterStopInSearch(text, tour.firstStop) ||
        filterStopInSearch(text, tour.lastStop)
    );
};

const containsFilterTerm = (item: Tour, statusFilter: string[]) =>
    statusFilter.length === 0 || statusFilter.includes(item.status);

export const matchesTourTimeRange = (tour: Tour, range?: DateTimeRange) => {
    if (!range) {
        return true;
    }
    if (!tour.firstStop || !tour.lastStop) {
        return false;
    }
    const start = DateTime.fromISO(tour.firstStop.startTarget).toMillis();
    const end = DateTime.fromISO(tour.lastStop.endTarget).toMillis();
    return Number.isFinite(start) && Number.isFinite(end) && end >= start && start < range.end && end >= range.start;
};

export const useFilteredTours = <T extends Tour>({
    tours,
    vehicles,
    drivers,
    search,
    statusFilter,
    range,
    selectedTourId,
}: {
    tours: T[];
    vehicles: Vehicle[];
    drivers?: Driver[];
    search: string;
    statusFilter: string[];
    range?: DateTimeRange;
    selectedTourId?: string;
}) => {
    const filteredTours = useMemo(
        () =>
            tours
                .filter(tour => containsSearchTerm({ tour, vehicles, drivers, search }))
                .filter(tour => containsFilterTerm(tour, statusFilter) && matchesTourTimeRange(tour, range)),
        [drivers, search, statusFilter, tours, vehicles, range?.start, range?.end]
    );
    const activeTour = useMemo(() => tours.find(item => item.id === selectedTourId), [selectedTourId, tours]);

    return {
        filteredTours,
        activeTour,
    };
};
